/**
 * WhatsApp utility functions for Moya Fit
 * Generates wa.me links with pre-filled messages
 * Supports DB-loaded templates from notification settings
 */

/** Normalize Turkish phone to international format for WhatsApp (905XXXXXXXXX) */
export const normalizePhoneForWhatsApp = (phone: string): string => {
  if (!phone) return '';
  let cleaned = phone.replace(/[\s\-\(\)\.+]/g, '');
  if (cleaned.startsWith('90') && cleaned.length > 10) {
    // already has country code
  } else if (cleaned.startsWith('0')) {
    cleaned = '90' + cleaned.substring(1);
  } else if (cleaned.length === 10) {
    cleaned = '90' + cleaned;
  }
  return cleaned;
};

/** Open WhatsApp with a pre-filled message */
export const openWhatsApp = (phone: string, message: string) => {
  const normalized = normalizePhoneForWhatsApp(phone);
  if (!normalized) return;
  const encoded = encodeURIComponent(message);

  // Mobile: wa.me link — OS intercepts and opens WhatsApp app directly.
  // Desktop: web.whatsapp.com/send — avoids api.whatsapp.com redirect blocks.
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  if (isMobile) {
    const url = `https://wa.me/${normalized}?text=${encoded}`;
    window.location.href = url;
  } else {
    const url = `https://web.whatsapp.com/send?phone=${normalized}&text=${encoded}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

/** Fill template placeholders with values */
export const fillTemplate = (template: string, values: Record<string, string | number>): string => {
  let result = template;
  for (const [key, value] of Object.entries(values)) {
    result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), String(value));
  }
  return result;
};

// Cached templates from DB
let cachedTemplates: Record<string, string> | null = null;
let cacheTimestamp = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/** Load WhatsApp templates from notification settings */
export const loadWhatsAppTemplates = async (): Promise<Record<string, string>> => {
  const now = Date.now();
  if (cachedTemplates && (now - cacheTimestamp) < CACHE_TTL) {
    return cachedTemplates;
  }
  
  try {
    const token = localStorage.getItem('token');
    if (!token) return getDefaultTemplates();
    
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/whatsapp-templates`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    if (response.ok) {
      cachedTemplates = await response.json();
      cacheTimestamp = now;
      return cachedTemplates!;
    }
  } catch {
    // Fall back to defaults
  }
  
  return getDefaultTemplates();
};

/** Default templates (fallback when API unavailable) */
const getDefaultTemplates = (): Record<string, string> => ({
  payment_reminder: 'Merhaba {member_name},\n\nMoya Fit\'ten hatırlatma:\nKalan borcunuz: {remaining_amount} ₺\n\nÖdemenizi en kısa sürede yapmanızı rica ederiz.\n\nTeşekkürler,\nMoya Fit',
  sale_confirmation: 'Merhaba {member_name},\n\nMoya Fit\'e hoş geldiniz!\n\nPaketiniz: {package_name}\nToplam Tutar: {total_amount} ₺\nÖdenen: {paid_amount} ₺\nKalan: {remaining_amount} ₺\n\nİyi antrenmanlar!\nMoya Fit',
  general_member_contact: 'Merhaba {member_name}, Moya Fit\'ten yazıyoruz.',
});

/** Generate payment reminder using DB template */
export const paymentReminderMessage = async (memberName: string, amount: number): Promise<string> => {
  const templates = await loadWhatsAppTemplates();
  return fillTemplate(templates.payment_reminder || getDefaultTemplates().payment_reminder, {
    member_name: memberName,
    remaining_amount: amount.toFixed(2),
    branch_name: 'Moya Fit',
  });
};

/** Generate sale confirmation using DB template */
export const saleConfirmationMessage = async (
  memberName: string,
  packageName: string,
  amount: number,
  paid: number
): Promise<string> => {
  const templates = await loadWhatsAppTemplates();
  return fillTemplate(templates.sale_confirmation || getDefaultTemplates().sale_confirmation, {
    member_name: memberName,
    package_name: packageName,
    total_amount: amount.toFixed(2),
    paid_amount: paid.toFixed(2),
    remaining_amount: (amount - paid).toFixed(2),
    branch_name: 'Moya Fit',
  });
};

/** Generate generic notification message */
export const notificationWhatsAppMessage = (notifMessage: string): string => {
  return `Moya Fit Bildirim:\n\n${notifMessage}`;
};

/** Generate general member contact message using DB template */
export const generalMemberMessage = async (memberName: string): Promise<string> => {
  const templates = await loadWhatsAppTemplates();
  return fillTemplate(templates.general_member_contact || getDefaultTemplates().general_member_contact, {
    member_name: memberName,
    message_content: '',
    branch_name: 'Moya Fit',
  });
};
